import { AxiosInstance, AxiosRequestConfig } from "axios";
import { compact, isEmpty } from "lodash";
import pluralize from "pluralize";
import { useQuery, useQueryClient, UseQueryOptions } from "react-query";
import { api as xolaApi } from "../api/api";
import { populateResource } from "./helper";

interface ExcludedApiParams {
    enabled?: never; // This enforces that the "enabled" property cannot exist
    staleTime?: never;
    cacheTime?: never;
    keepPreviousData?: never;
}

type ApiParams = (Record<string, any> & ExcludedApiParams) | null;

export interface ResourceFetchOptions<T> extends UseQueryOptions<T> {
    api?: AxiosInstance;
    apiConfig?: AxiosRequestConfig;
}

export const useResource = <T>(path: any[], params?: ApiParams, options?: ResourceFetchOptions<T>) => {
    const api = options?.api ?? xolaApi;
    const queryClient = useQueryClient();
    const [collectionName, id] = path.slice(-2);
    const isEnabled = !!id && options?.enabled;

    const query = useQuery<T>(
        compact([collectionName, id, ...(isEmpty(params) ? [] : [params])]),
        async () => {
            const url = path.join("/"); // TODO: Remove leading / because we will add it below
            const { data } = await api.get<T>(`/${url}`, { params, ...options?.apiConfig });

            if (params?.expand) {
                const dataKeyString: { [key: string]: any } = data;
                const expandParams: string[] = params.expand.split(",");
                for (const paramName of expandParams) {
                    if (dataKeyString[paramName]) {
                        populateResource(pluralize(paramName, 2), dataKeyString[paramName]);
                    }
                }
            }

            return data;
        },
        {
            // Tries to find the existing resource in the cache (without using query params)
            // that may be set by `useCollection`.
            placeholderData: () => queryClient.getQueryData(path),
            ...options,
            enabled: isEnabled, // Always leave this after options because we already check options.enabled above
        },
    );

    return query;
};
